import React from 'react';

import type { AdditionalFilterInDropdown } from '../TableFilterBar/AdditionalFilters/constants';
import { isAdditionalFilterDropdown } from '../TableFilterBar/AdditionalFilters/utils';
import type { TopBarFilter } from './FilterValueChips';
import { AndGroupWrapper, OrSeparator } from './JunctionIndicators';
import type { FilterRuleTree } from './ruleTree';

export type RuleTreeRendererProps = {
  ruleTree: FilterRuleTree;
  renderFilterGroup: (filterId: string) => React.ReactNode;
};

const RuleTreeRenderer: React.FC<RuleTreeRendererProps> = ({ ruleTree, renderFilterGroup }) => {
  const { groups } = ruleTree;

  if (groups.length === 0) return null;

  return (
    <>
      {groups.map((group, groupIndex) => (
        <React.Fragment key={groupIndex}>
          {groupIndex > 0 && <OrSeparator />}
          <AndGroupWrapper showRail={groups.length > 1}>
            {group.conditions.map((condition) => (
              <React.Fragment key={condition.filterId}>{renderFilterGroup(condition.filterId)}</React.Fragment>
            ))}
          </AndGroupWrapper>
        </React.Fragment>
      ))}
    </>
  );
};

export type UseRuleTreeRenderMapArgs = {
  topBarFilters: TopBarFilter[];
  selectedFilters: AdditionalFilterInDropdown[];
  renderTopBarGroup: (filter: TopBarFilter) => React.ReactNode;
  renderDropdownGroup: (filter: AdditionalFilterInDropdown) => React.ReactNode;
  renderNonDropdownGroup: (filter: AdditionalFilterInDropdown) => React.ReactNode;
};

export const buildFilterGroupMap = ({
  topBarFilters,
  selectedFilters,
  renderTopBarGroup,
  renderDropdownGroup,
  renderNonDropdownGroup,
}: UseRuleTreeRenderMapArgs): Map<string, React.ReactNode> => {
  const map = new Map<string, React.ReactNode>();

  topBarFilters.forEach((filter) => {
    map.set(filter.searchParam, renderTopBarGroup(filter));
  });

  selectedFilters.forEach((filter) => {
    const key = filter.searchParam as string;
    map.set(key, isAdditionalFilterDropdown(filter) ? renderDropdownGroup(filter) : renderNonDropdownGroup(filter));
  });

  return map;
};

export const createFilterGroupRenderer = (
  filterGroupMap: Map<string, React.ReactNode>
): ((filterId: string) => React.ReactNode) => {
  return (filterId: string): React.ReactNode => filterGroupMap.get(filterId) ?? null;
};

export default RuleTreeRenderer;
